<template>
  <div class="warehouse-page">
    <p v-if="overview.sapB1" class="banner info">
      SAP B1 host {{ overview.sapB1.hostname }} ({{ overview.sapB1.host }}).
      Login URL {{ overview.sapB1.baseURL }}/Login.
      Credentials {{ overview.sapB1.companyDBSet && overview.sapB1.userSet ? 'are set' : 'are not set yet (SAP_B1_COMPANY_DB / USERNAME / PASSWORD in app/.env)' }}.
    </p>

    <section class="pipeline">
      <div class="step">
        <span class="n">1</span>
        <div>
          <strong>SAP B1 live</strong>
          <p>Items, warehouses, invoices and goods receipts from Selgate Service Layer</p>
        </div>
      </div>
      <i class="fa-solid fa-arrow-right"></i>
      <div class="step">
        <span class="n">2</span>
        <div>
          <strong>Field mapping</strong>
          <p>SAP fields mapped onto SKU, qty, committed, barcode, cost, location, UOM</p>
        </div>
      </div>
      <i class="fa-solid fa-arrow-right"></i>
      <div class="step">
        <span class="n">3</span>
        <div>
          <strong>SECTOR warehouse</strong>
          <p>{{ overview.counts.items }} items · {{ overview.counts.stockRows }} stock rows · {{ overview.counts.sales }} sales</p>
        </div>
      </div>
      <i class="fa-solid fa-arrow-right"></i>
      <div class="step">
        <span class="n">4</span>
        <div>
          <strong>Dashboard & CSV</strong>
          <p>Visibility, hygiene, GP% and export</p>
        </div>
      </div>
    </section>

    <div class="grid">
      <article v-for="source in overview.sources" :key="source.code" class="card">
        <div class="card-head">
          <div>
            <h3>{{ source.name }}</h3>
            <p>{{ source.environment }} · {{ source.code }}</p>
          </div>
          <span class="pill" :class="{ on: source.isActive }">{{ source.isActive ? 'Active' : 'Inactive' }}</span>
        </div>
        <dl>
          <div><dt>Items</dt><dd>{{ source.itemCount }}</dd></div>
          <div><dt>Stock rows</dt><dd>{{ source.stockRows }}</dd></div>
          <div><dt>Mapping</dt><dd>{{ source.mappingPct }}%</dd></div>
          <div><dt>Last sync</dt><dd>{{ formatTime(source.lastSyncAt) }}</dd></div>
        </dl>
        <p class="muted">{{ source.description }}</p>
        <p v-if="source.endpointUrl" class="endpoint">{{ source.endpointUrl }}</p>
        <div v-if="source.code === 'sap_selgate'" class="card-actions">
          <button class="btn ghost" type="button" :disabled="busy" @click="testSource(source.code)">Test connection</button>
          <button class="btn" type="button" :disabled="busy" @click="pullSource(source.code)">Pull from SAP B1</button>
        </div>
        <div v-else-if="source.code === 'scms_clinic'" class="card-actions">
          <button class="btn ghost" type="button" :disabled="busy" @click="testSource(source.code)">Test SCMS</button>
          <button class="btn" type="button" :disabled="busy" @click="pullSource(source.code)">Pull clinic meds</button>
        </div>
        <div v-else-if="source.code === 'sdms_dental'" class="card-actions">
          <button class="btn ghost" type="button" :disabled="busy" @click="testSource(source.code)">Test SDMS</button>
          <button class="btn" type="button" :disabled="busy" @click="pullSource(source.code)">Pull dental meds</button>
        </div>
      </article>
    </div>

    <p v-if="overview.scmsClinic" class="banner info">
      SCMS clinics: {{ overview.scmsClinic.clinics }} mapped IDs
      ({{ (overview.scmsClinic.clinicIds || []).join(', ') }}).
      Endpoint {{ overview.scmsClinic.endpoint }}.
    </p>
    <p v-if="overview.sdmsDental" class="banner info">
      SDMS dentals: {{ overview.sdmsDental.dentals }} mapped IDs
      ({{ (overview.sdmsDental.dentalIds || []).join(', ') }} — 1 Putrajaya, 3 Unisel).
      Endpoint {{ overview.sdmsDental.endpoint }}.
    </p>

    <section class="card block">
      <h2>Ingest endpoints</h2>
      <p class="muted">SECTOR warehouse is the frontend for SAP B1. Pull from Selgate Service Layer, or POST ingest payloads using Sources & Mapping.</p>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Path</th>
            <th>Purpose</th>
            <th>Auth</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="endpoint in overview.endpoints" :key="endpoint.path">
            <td><code>{{ endpoint.method }}</code></td>
            <td><code>{{ endpoint.path }}</code></td>
            <td>{{ endpoint.purpose }}</td>
            <td>{{ endpoint.auth }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="split">
      <section class="card block">
        <h2>CSV import</h2>
        <p class="muted">Use source-system column headers if mapping is saved, or SECTOR names (sku, item_name, quantity, location...).</p>
        <form class="form" @submit.prevent="importCsv">
          <label>Source
            <select v-model="csv.source" required>
              <option value="">Select source</option>
              <option v-for="source in overview.sources" :key="source.code" :value="source.code">{{ source.name }}</option>
            </select>
          </label>
          <label>Type
            <select v-model="csv.type">
              <option value="items">Product master</option>
              <option value="inventory">Inventory / stock</option>
              <option value="sales">Sales</option>
            </select>
          </label>
          <label>CSV file
            <input type="file" accept=".csv,text/csv" @change="onFile">
          </label>
          <button class="btn" type="submit" :disabled="!csv.text">Import CSV</button>
        </form>
      </section>

      <section class="card block">
        <h2>Manual JSON sync</h2>
        <p class="muted">Paste a payload and push it through the same pipeline SAP will use.</p>
        <form class="form" @submit.prevent="manualSync">
          <label>Source
            <select v-model="json.source" required>
              <option value="">Select source</option>
              <option v-for="source in overview.sources" :key="source.code" :value="source.code">{{ source.name }}</option>
            </select>
          </label>
          <label>JSON
            <textarea v-model="json.text" rows="8" spellcheck="false" placeholder='{ "items": [], "inventory": [], "sales": [] }'></textarea>
          </label>
          <button class="btn" type="submit">Run sync</button>
        </form>
      </section>
    </div>

    <section class="card block">
      <div class="card-head">
        <h2>Sync history</h2>
        <button class="btn ghost" type="button" @click="load">Refresh</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Source</th>
            <th>Type</th>
            <th>Status</th>
            <th>In</th>
            <th>Upserted</th>
            <th>Skipped</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in overview.logs" :key="log.id">
            <td>{{ formatTime(log.created_at) }}</td>
            <td>{{ log.source_name || '—' }}</td>
            <td>{{ log.sync_type }}</td>
            <td>{{ log.status }}</td>
            <td>{{ log.records_in }}</td>
            <td>{{ log.records_upserted }}</td>
            <td>{{ log.records_skipped }}</td>
            <td>{{ log.message || '—' }}</td>
          </tr>
          <tr v-if="!overview.logs?.length">
            <td colspan="8" class="empty">No syncs yet. Import CSV, run a JSON sync, or POST to the ingest endpoints.</td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import warehouseApi from '@/utils/warehouseApi'

const error = ref('')
const notice = ref('')
const busy = ref(false)
const overview = ref({
  counts: { items: 0, stockRows: 0, sales: 0, sources: 0 },
  sources: [],
  endpoints: [],
  logs: [],
  sapB1: null,
  scmsClinic: null,
  sdmsDental: null
})
const csv = reactive({ source: '', type: 'inventory', text: '' })
const json = reactive({
  source: '',
  text: JSON.stringify({ items: [], inventory: [], sales: [] }, null, 2)
})

function formatTime(value) {
  if (!value) return 'Never'
  return new Date(value).toLocaleString()
}

async function load() {
  try {
    error.value = ''
    const { data } = await warehouseApi.get('/api/warehouse/overview')
    overview.value = data
    if (!csv.source && data.sources?.[0]) csv.source = data.sources[0].code
    if (!json.source && data.sources?.[0]) json.source = data.sources[0].code
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load data flow.'
  }
}

function onFile(event) {
  const file = event.target.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => { csv.text = String(reader.result || '') }
  reader.readAsText(file)
}

async function importCsv() {
  try {
    error.value = ''
    const { data } = await warehouseApi.post('/api/warehouse/import/csv', {
      source: csv.source,
      type: csv.type,
      csv: csv.text
    })
    notice.value = `Imported ${data.rowCount} rows (${data.upserted || data.inserted || 0} saved).`
    await load()
  } catch (err) {
    error.value = err.response?.data?.error || 'CSV import failed.'
  }
}

async function manualSync() {
  try {
    error.value = ''
    const payload = JSON.parse(json.text)
    const { data } = await warehouseApi.post('/api/warehouse/sync', { source: json.source, ...payload })
    notice.value = `Sync complete for ${data.source}.`
    await load()
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Sync failed.'
  }
}

async function testSource(code) {
  try {
    busy.value = true
    error.value = ''
    const { data } = await warehouseApi.post(`/api/warehouse/sources/${code}/test`)
    if (code === 'scms_clinic') {
      notice.value = `SCMS OK — clinic ${data.clinicId} ${data.clinicName || ''} · ${data.total || 0} medications.`
    } else if (code === 'sdms_dental') {
      notice.value = `SDMS OK — dental ${data.dentalId} ${data.dentalName || ''} · ${data.total || 0} medications.`
    } else {
      notice.value = `SAP B1 login OK${data.version ? ` (version ${data.version})` : ''}.`
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Connection test failed.'
  } finally {
    busy.value = false
  }
}

async function pullSource(code) {
  try {
    busy.value = true
    error.value = ''
    notice.value = code === 'scms_clinic'
      ? 'Pulling SCMS clinic medications. This can take a few minutes…'
      : code === 'sdms_dental'
        ? 'Pulling SDMS dental medications…'
        : 'Pulling from SAP B1. This can take several minutes…'
    const { data } = await warehouseApi.post(`/api/warehouse/sources/${code}/pull`, {}, { timeout: 600000 })
    if (data.async || data.started) {
      notice.value = data.message
        || `Pull started for ${code}. Refresh Sync history in about a minute — prod nginx closes long requests around 60s.`
      // Give the job a moment, then refresh overview / sync logs
      await new Promise((resolve) => setTimeout(resolve, 2500))
      await load()
      return
    }
    if (code === 'scms_clinic') {
      notice.value = `Pulled ${data.pulled?.items || 0} clinic products, ${data.pulled?.inventory || 0} clinic stock rows (${data.pulled?.withStock || 0} with on-hand) across ${data.pulled?.clinics || 0} clinics.`
    } else if (code === 'sdms_dental') {
      notice.value = `Pulled ${data.pulled?.items || 0} dental products, ${data.pulled?.inventory || 0} dental stock rows (${data.pulled?.withStock || 0} with on-hand) across ${data.pulled?.dentals || 0} dentals.`
    } else {
      notice.value = `Pulled ${data.pulled?.items || 0} items, ${data.pulled?.inventory || 0} stock rows, ${data.pulled?.sales || 0} sales, ${data.pulled?.warehouses || 0} warehouses from SAP B1.`
    }
    await load()
  } catch (err) {
    error.value = err.response?.data?.error || 'Pull failed.'
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.pipeline {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr auto 1fr;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 16px;
}
.pipeline > i { align-self: center; color: #94a3b8; }
.step, .card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.step { display: flex; gap: 10px; padding: 14px; }
.n {
  width: 28px; height: 28px; border-radius: 999px; background: #e0f2fe; color: #0369a1;
  display: inline-flex; align-items: center; justify-content: center; font-weight: 700; flex-shrink: 0;
}
.step p, .muted { margin: 4px 0 0; color: #64748b; font-size: 0.75rem; }
.grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; margin-bottom: 16px; }
.card { padding: 16px; }
.card.block { margin-bottom: 16px; }
.card-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 8px; }
h2, h3 { margin: 0; color: #1e293b; }
h2 { font-size: 1rem; margin-bottom: 8px; }
h3 { font-size: 0.95rem; }
.pill { font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; background: #fee2e2; color: #991b1b; }
.pill.on { background: #dcfce7; color: #166534; }
dl { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 12px 0; }
dt { font-size: 0.7rem; color: #64748b; }
dd { margin: 0; font-weight: 600; color: #1e293b; }
.endpoint { font-family: monospace; font-size: 0.72rem; color: #0369a1; word-break: break-all; }
.split { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form { display: grid; gap: 10px; }
.form label { display: flex; flex-direction: column; gap: 4px; font-size: 0.75rem; color: #64748b; }
.form select, .form input, .form textarea, .btn {
  padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 0.813rem; background: white;
}
.form textarea { font-family: monospace; }
.btn { background: #0ea5e9; color: white; border-color: #0ea5e9; cursor: pointer; width: fit-content; }
.btn.ghost { background: white; color: #475569; }
table { width: 100%; border-collapse: collapse; margin-top: 8px; }
th, td { padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 0.78rem; text-align: left; }
th { color: #475569; }
code { font-size: 0.72rem; }
.empty { text-align: center; color: #64748b; padding: 20px; }
.banner { padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; font-size: 0.813rem; }
.banner.info { background: #eff6ff; color: #1e40af; }
.card-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.card-actions .btn { width: auto; }
@media (max-width: 960px) {
  .pipeline, .split { grid-template-columns: 1fr; }
  .pipeline > i { display: none; }
}
</style>
